import { useEffect, useState } from 'react'
import { loadStripe, type Stripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { App as AntdApp, Button, Modal } from 'antd'
import { useTranslation } from 'react-i18next'

import apiClient from '../api/client'

function CardForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    if (!stripe || !elements) return
    setSubmitting(true)
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    })
    if (error) {
      message.error(error.message || t('portal.card.failed', 'Karte konnte nicht gespeichert werden'))
      setSubmitting(false)
      return
    }
    if (setupIntent && setupIntent.status === 'succeeded') {
      try {
        await apiClient.post('/api/v1/portal/payment-methods/stripe/finalize', { setup_intent_id: setupIntent.id })
        message.success(t('portal.card.saved', 'Kreditkarte hinterlegt ✓'))
        onDone()
      } catch {
        message.error(t('portal.card.finalizeFailed', 'Speichern fehlgeschlagen'))
        setSubmitting(false)
      }
    } else {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <PaymentElement />
      <Button type="primary" block loading={submitting} onClick={submit} style={{ marginTop: 16 }}>
        {t('portal.card.save', 'Karte speichern')}
      </Button>
    </div>
  )
}

export default function StripeCardSetup({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null)
  const [clientSecret, setClientSecret] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) { setClientSecret(''); return }
    setLoading(true)
    apiClient.post<{ data: { client_secret: string; publishable_key: string } }>(
      '/api/v1/portal/payment-methods/stripe/setup-intent')
      .then((res) => {
        const d = res.data.data
        setStripePromise(loadStripe(d.publishable_key))
        setClientSecret(d.client_secret)
      })
      .catch((e: { response?: { data?: { error?: string } } }) =>
        message.error(e?.response?.data?.error || t('portal.card.setupFailed', 'Stripe-Einrichtung fehlgeschlagen')))
      .finally(() => setLoading(false))
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal open={open} onCancel={onClose} footer={null} destroyOnClose
      title={t('portal.card.title', 'Kreditkarte hinterlegen')}>
      {stripePromise && clientSecret ? (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CardForm onDone={() => { onDone(); onClose() }} />
        </Elements>
      ) : (
        <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
          {loading ? t('common.loading', 'Lädt …') : t('portal.card.unavailable', 'Stripe nicht verfügbar')}
        </div>
      )}
    </Modal>
  )
}
